import type { Edge, Node } from '@xyflow/react';
import type {
  SegmentationCondition,
  SegmentationConnector,
  SegmentationCriteria,
  SegmentationGroup,
  SegmentationLayout,
} from '@/shared/types/segmentation';

/**
 * Topologia do canvas é uma CADEIA linear, não um grafo livre: cada grupo novo
 * é anexado ao fim, conectado ao anterior por uma aresta E/OU -- não dá pra
 * "religar" arestas arrastando (arrastar só reposiciona visualmente). Isso
 * elimina ambiguidade de precedência AND/OR e mantém a query builder do
 * backend (que também dobra a cadeia da esquerda pra direita) simples e
 * previsível. Ver "Semântica de avaliação" no plano.
 */

export interface GroupNodeData extends Record<string, unknown> {
  group: SegmentationGroup;
}

export type GroupNode = Node<GroupNodeData, 'group'>;

const DEFAULT_X_SPACING = 340;
const DEFAULT_Y = 120;

export function defaultPositionForIndex(index: number): { x: number; y: number } {
  return { x: index * DEFAULT_X_SPACING, y: DEFAULT_Y };
}

export function groupsToNodes(
  groups: SegmentationGroup[],
  positions?: SegmentationLayout['positions'],
): GroupNode[] {
  return groups.map((group, index) => ({
    id: group.id,
    type: 'group',
    position: positions?.[group.id] ?? defaultPositionForIndex(index),
    data: { group },
  }));
}

/** Mescla domínio (groups/positions) no estado visual do React Flow, preservando
 * measured/width/height/selected para os nodes não serem tratados como
 * "não inicializados" (erro #015) a cada re-render. */
export function reconcileGroupNodes(
  groups: SegmentationGroup[],
  positions: SegmentationLayout['positions'] | undefined,
  previous: GroupNode[],
): GroupNode[] {
  const prevById = new Map(previous.map((node) => [node.id, node]));
  return groups.map((group, index) => {
    const prev = prevById.get(group.id);
    const position = prev?.dragging
      ? prev.position
      : (positions?.[group.id] ?? prev?.position ?? defaultPositionForIndex(index));

    const next: GroupNode = {
      id: group.id,
      type: 'group',
      position,
      data: { group },
    };

    if (!prev) return next;

    return {
      ...next,
      measured: prev.measured,
      width: prev.width,
      height: prev.height,
      selected: prev.selected,
      dragging: prev.dragging,
    };
  });
}

export interface ConnectorEdgeData extends Record<string, unknown> {
  connector: SegmentationConnector;
  /** Índice em `connectors[]` — a aresta custom usa pra toggle E/Ou. */
  chainIndex: number;
}

export type ConnectorEdge = Edge<ConnectorEdgeData, 'connector'>;

export function groupsToEdges(groups: SegmentationGroup[], connectors: SegmentationConnector[]): ConnectorEdge[] {
  const edges: ConnectorEdge[] = [];
  for (let i = 0; i < groups.length - 1; i++) {
    const connector = connectors[i] ?? 'AND';
    edges.push({
      id: `${groups[i].id}->${groups[i + 1].id}`,
      type: 'connector',
      source: groups[i].id,
      target: groups[i + 1].id,
      data: { connector, chainIndex: i },
    });
  }
  return edges;
}

export function nodesToPositions(nodes: Pick<GroupNode, 'id' | 'position'>[]): NonNullable<SegmentationLayout['positions']> {
  const positions: NonNullable<SegmentationLayout['positions']> = {};
  for (const node of nodes) {
    positions[node.id] = { x: node.position.x, y: node.position.y };
  }
  return positions;
}

export function buildCriteria(groups: SegmentationGroup[], connectors: SegmentationConnector[]): SegmentationCriteria {
  return { groups, connectors: connectors.slice(0, Math.max(groups.length - 1, 0)) };
}

let localIdCounter = 0;

/** IDs só precisam ser únicos dentro de uma sessão de edição -- não são persistidos
 * como ObjectId, então timestamp+contador é suficiente (sem dependência de uuid). */
export function generateLocalId(prefix: string): string {
  localIdCounter += 1;
  return `${prefix}_${Date.now().toString(36)}_${localIdCounter}`;
}

export function createEmptyCondition(): SegmentationCondition {
  return { id: generateLocalId('cond'), field: '', operator: 'equals', value: undefined };
}

export function createEmptyGroup(): SegmentationGroup {
  return { id: generateLocalId('grp'), condition: 'AND', conditions: [createEmptyCondition()] };
}
